import React from 'react'
import { Route, Switch } from 'react-router-dom'

import { SitesToastContainer } from 'src/shared/js/alertUtils'
import { t } from 'src/translations'

import {
  AccountPageContent,
  ContextualAccountPage
} from 'public/components/default_template/AccountPage'
import {
  CheckoutPageContent,
  ContextualCheckoutPage
} from 'public/components/default_template/CheckoutPage'
import {
  ConfirmationPageContent,
  ContextualConfirmationPage
} from 'public/components/default_template/ConfirmationPage'
import { GlobalNavContextProvider } from 'public/components/default_template/GlobalNavContext'
import RestaurantProfilePage from 'public/components/default_template/RestaurantProfilePage'
import { SiftContextProvider } from 'public/components/default_template/meta/sift/SiftContext'
import NavContent from 'public/components/default_template/nav/NavContent'
import { OrderPageContexts } from 'public/components/default_template/online_ordering/pages/OrderPageContexts'
import RestaurantProfilePageWrapper, {
  WrappedProfilePageProps
} from 'public/components/default_template/restaurant_profile/RestaurantProfilePageWrapper'
import { BreadcrumbItem } from 'public/components/default_template/restaurant_profile/breadcrumbs/Breadcrumbs'
import { formatRXName } from 'public/components/default_template/restaurant_profile/formatting'
import { RestaurantProfileRestaurantType } from 'public/components/default_template/restaurant_profile/profileUtils'
import { CustomerContextProvider } from 'public/components/online_ordering/CustomerContext'
import NotFound404 from 'shared/components/errors/NotFound404'

import { TOAST_APP_ORDER_PATH, Channel } from './siteUtilities'
import { RequestContext } from 'src/lib/js/context'
import { getHost } from 'src/lib/js/utilities'
import { resources } from 'config'

type ToastAppOrderRoutesProps = {
  staticContext?: RequestContext
}

/**
 * Determine if the current request is on book.toasttab.com or toast.app
 * Returns bookHost if on book.toasttab.com, otherwise returns toastAppHost
 * Exported for testing
 */
export const getBookOrAppDomainHost = (
  staticContext?: RequestContext
): string => {
  const currentHost = getHost(staticContext)
  const bookHost = resources.bookHost
  const toastAppHost = resources.toastAppHost || 'toast.app'

  if (bookHost && currentHost === bookHost) {
    return bookHost
  }

  return toastAppHost
}

/**
 * Logo source for Toast Local branding used across all order pages.
 *
 * This URL uses a content-based hash (9c5a8cca...) for cache busting and version control.
 * The hash is stable and will not change unless the logo asset itself changes, at which
 * point this constant should be updated with the new hash. This pattern is used throughout
 * the codebase for CDN assets (see Hero.tsx, TakeoutCTAV2.tsx for similar examples).
 *
 * Exported for use in tests to avoid URL duplication.
 */
export const TOAST_LOCAL_LOGO_SRC =
  'https://cdn.toasttab.com/static/9c5a8cca339ecb1d70c3f8cff6b3d78ce0ce5205/images/guest/local-app/toast-local-logo.svg'

/**
 * Build breadcrumb items for order pages on toast.app/book.toasttab.com.
 * Structure: Home → City → Restaurant → Online Ordering
 *
 * @param restaurant - Restaurant data for building breadcrumbs
 * @param host - Domain host (e.g., 'toast.app') for constructing absolute URLs
 * @returns Array of breadcrumb items
 */
export const buildOrderPageBreadcrumbs = (
  restaurant: RestaurantProfileRestaurantType,
  host: string
): BreadcrumbItem[] => {
  const items: BreadcrumbItem[] = []

  // 1. Home link - links to domain root (uses full URL to work with reverse proxy)
  // Note: host may include port for local-dev (e.g., "local-dev.eng.toastteam.com:24103")
  items.push({
    label: t('breadcrumbs.home'),
    href: `https://${host}`
  })

  // 2. City link - links to city page (if location available)
  if (restaurant.location?.city && restaurant.location?.state) {
    const city = restaurant.location.city
    const state = restaurant.location.state
    // Format: /cities/{city}-{state} (e.g., /cities/boston-ma)
    const citySlug = `${city}-${state}`.toLowerCase().replace(/\s+/g, '-')
    items.push({
      label: city,
      href: `https://${host}/cities/${citySlug}`
    })
  }

  // 3. Restaurant link - links BACK to profile page (not order page)
  // This allows users to see full restaurant info from order page
  items.push({
    label: formatRXName(restaurant.name) || t('breadcrumbs.restaurant'),
    href: restaurant.shortUrl ? `https://${host}/r/${restaurant.shortUrl}` : ''
  })

  // 4. Online Ordering - current page (non-clickable)
  items.push({
    label: t('breadcrumbs.online-ordering'),
    href: '' // Empty href = rendered as plain text
  })

  return items
}

/**
 * Order routing component for /r/:slug/order pattern on toast.app and book.toasttab.com.
 * Uses domain configuration to customize paths, branding, and behavior.
 */
const ToastAppOrderRoutes = ({ staticContext }: ToastAppOrderRoutesProps) => {
  const host = getBookOrAppDomainHost(staticContext)
  const basePath = '/order'
  const logoLink = `https://${host}/`
  const channel = Channel.OO
  const ctaUrl = '/'
  const respectToastLocalConfig = false

  // Order page component - configured per domain
  const orderComponent = (
    <RestaurantProfilePageWrapper
      respectToastLocalConfig={respectToastLocalConfig}
      baseHomePath={basePath}
      baseOrderPath={basePath}
      host={host}
      useSlugFirstPattern
    >
      {({
        restaurantProfileData,
        nearbyRxs,
        optionalSiteData
      }: WrappedProfilePageProps) => (
        <OrderPageContexts channel={channel}>
          <RestaurantProfilePage
            restaurantProfileData={restaurantProfileData}
            nearbyRxs={nearbyRxs}
            optionalSiteData={optionalSiteData}
            showOrderContent
            breadcrumbItems={buildOrderPageBreadcrumbs(
              restaurantProfileData,
              host
            )}
            hideRXCarousel
            hideFooter
            logoSrc={TOAST_LOCAL_LOGO_SRC}
            logoLink={logoLink}
            useToastAppHeader
          />
        </OrderPageContexts>
      )}
    </RestaurantProfilePageWrapper>
  )

  // Checkout page component - configured per domain
  const checkoutComponent = (
    <RestaurantProfilePageWrapper
      respectToastLocalConfig={respectToastLocalConfig}
      baseHomePath={basePath}
      baseOrderPath={basePath}
      host={host}
      useSlugFirstPattern
    >
      {() => (
        <ContextualCheckoutPage>
          <CheckoutPageContent
            nav={
              <NavContent
                navType='stickyNav'
                logoLink={logoLink}
                logoSrc={TOAST_LOCAL_LOGO_SRC}
                logoLinkSameTab
                shouldShowPreviewBanner={false}
                withCart={true}
              />
            }
          />
        </ContextualCheckoutPage>
      )}
    </RestaurantProfilePageWrapper>
  )

  // Confirmation page component - configured per domain
  const confirmationComponent = (
    <RestaurantProfilePageWrapper
      respectToastLocalConfig={respectToastLocalConfig}
      baseHomePath={basePath}
      baseOrderPath={basePath}
      host={host}
      useSlugFirstPattern
    >
      {() => (
        <ContextualConfirmationPage>
          <ConfirmationPageContent
            nav={
              <NavContent
                navType='stickyNav'
                logoLink={logoLink}
                logoSrc={TOAST_LOCAL_LOGO_SRC}
                logoLinkSameTab
                shouldShowPreviewBanner={false}
              />
            }
          />
        </ContextualConfirmationPage>
      )}
    </RestaurantProfilePageWrapper>
  )

  // Account page component - configured per domain
  const accountComponent = (
    <RestaurantProfilePageWrapper
      respectToastLocalConfig={respectToastLocalConfig}
      baseHomePath={basePath}
      baseOrderPath={basePath}
      host={host}
      useSlugFirstPattern
    >
      {() => (
        <ContextualAccountPage>
          <AccountPageContent
            nav={
              <>
                <NavContent
                  navType='stickyNav'
                  logoLink={logoLink}
                  logoSrc={TOAST_LOCAL_LOGO_SRC}
                  logoLinkSameTab
                  shouldShowPreviewBanner={false}
                />
                <SitesToastContainer />
              </>
            }
          />
        </ContextualAccountPage>
      )}
    </RestaurantProfilePageWrapper>
  )

  return (
    <CustomerContextProvider>
      <SiftContextProvider>
        <GlobalNavContextProvider>
          <Switch>
            {/*
              /r/:slug/order/r-:rxGuid routes — the location-qualified form that
              /local/order/:slug/r-:rxGuid redirects to. These must stay ahead of
              the guid-free routes below: the optional-param routes
              (`:itemSlug?`, `:menuGuid?`) are greedy, so without these a
              '/r/:slug/order/r-<guid>' URL matches the menuProfile route and the
              location guid is silently read as a menu guid.
              Mirrors the ordering in ToastLocalRoutes.
            */}
            <Route
              exact
              path={`${TOAST_APP_ORDER_PATH}/:slug${basePath}/r-:rxGuid/checkout`}
              key='/r/slug/order/checkout'
              component={() => checkoutComponent}
            />
            <Route
              exact
              path={`${TOAST_APP_ORDER_PATH}/:slug${basePath}/r-:rxGuid/confirm/:pathOrderGuid`}
              key='/r/slug/order/confirm/:pathOrderGuid'
              component={() => confirmationComponent}
            />
            <Route
              exact
              path={`${TOAST_APP_ORDER_PATH}/:slug${basePath}/r-:rxGuid/confirm`}
              key='/r/slug/order/confirm'
              component={() => confirmationComponent}
            />
            <Route
              exact
              path={`${TOAST_APP_ORDER_PATH}/:slug${basePath}/r-:rxGuid/account`}
              key='/r/slug/order/account'
              component={() => accountComponent}
            />
            <Route
              exact
              path={`${TOAST_APP_ORDER_PATH}/:slug${basePath}/r-:rxGuid/:itemSlug(item-[^_]+_.+)?`}
              key='/r/slug/order/menuItemModalProfile'
              component={() => orderComponent}
            />
            <Route
              exact
              path={`${TOAST_APP_ORDER_PATH}/:slug${basePath}/r-:rxGuid/:menuSlug([^_]+_)?:menuGuid?`}
              key='/r/slug/order/menuProfile'
              component={() => orderComponent}
            />
            <Route
              exact
              path={`${TOAST_APP_ORDER_PATH}/:slug${basePath}/r-:rxGuid`}
              key='/r/slug/order/slugProfile'
              component={() => orderComponent}
            />
            {/* /r/:slug/order routes */}
            <Route
              exact
              path={`${TOAST_APP_ORDER_PATH}/:slug${basePath}/checkout`}
              key='/r/slug/order/checkout'
              component={() => checkoutComponent}
            />
            <Route
              exact
              path={`${TOAST_APP_ORDER_PATH}/:slug${basePath}/confirm/:pathOrderGuid`}
              key='/r/slug/order/confirm/:pathOrderGuid'
              component={() => confirmationComponent}
            />
            <Route
              exact
              path={`${TOAST_APP_ORDER_PATH}/:slug${basePath}/confirm`}
              key='/r/slug/order/confirm'
              component={() => confirmationComponent}
            />
            <Route
              exact
              path={`${TOAST_APP_ORDER_PATH}/:slug${basePath}/account`}
              key='/r/slug/order/account'
              component={() => accountComponent}
            />
            <Route
              exact
              path={`${TOAST_APP_ORDER_PATH}/:slug${basePath}/:itemSlug(item-[^_]+_.+)?`}
              key='/r/slug/order/menuItemModalProfile'
              component={() => orderComponent}
            />
            <Route
              exact
              path={`${TOAST_APP_ORDER_PATH}/:slug${basePath}/:menuSlug([^_]+_)?:menuGuid?`}
              key='/r/slug/order/menuProfile'
              component={() => orderComponent}
            />
            <Route
              exact
              path={`${TOAST_APP_ORDER_PATH}/:slug${basePath}`}
              key='/r/slug/order/slugProfile'
              component={() => orderComponent}
            />
            <Route component={() => <NotFound404 ctaUrl={ctaUrl} />} />
          </Switch>
        </GlobalNavContextProvider>
      </SiftContextProvider>
    </CustomerContextProvider>
  )
}

export default ToastAppOrderRoutes
